import { StandardEvents } from '@shopify/events';

/**
 * Loading feedback — the page never looks frozen (YouTube / GitHub / Amazon).
 *
 * One owner for "something is loading" across the store:
 *
 * 1. Top progress bar (.q-progress, styles in assets/custom-theme.css) for
 *    every page change — menu and nav items, product cards (Horizon opens
 *    them from script), filters chips, Check out, search. It starts on the
 *    tap and stays until the next page paints. Uses the Navigation API where
 *    the browser has it (covers script navigations too), else link clicks.
 * 2. In-page updates Horizon already announces with a promise:
 *    - product option change (productSelect) → price dimmed, Add to cart /
 *      Buy Now paused until the new variant is in, so the wrong variant can't
 *      be added mid-update;
 *    - filters / sort (collectionUpdate, searchUpdate) → product grid dimmed
 *      and paused until the new results render.
 *    The region gets `data-q-busy` + `aria-busy` (assets/custom-body.css),
 *    only after 120 ms so quick answers don't flicker; the bar runs as well.
 *
 * Every busy state has a safety release, and a page restored by Back
 * (bfcache) is reset.
 */

const REGION_DELAY_MS = 120;
const MAX_BUSY_MS = 15000;

class ProgressBar {
  /** @type {HTMLElement | null} */
  #el = null;
  #tasks = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #trickle;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #safety;
  #value = 0;

  get #bar() {
    if (!this.#el) {
      this.#el = document.createElement('div');
      this.#el.className = 'q-progress';
      this.#el.setAttribute('aria-hidden', 'true');
      document.body.append(this.#el);
    }
    return this.#el;
  }

  start() {
    this.#tasks += 1;
    if (this.#tasks > 1) return;
    clearTimeout(this.#safety);
    this.#safety = setTimeout(() => this.reset(), MAX_BUSY_MS);
    const bar = this.#bar;
    bar.dataset.state = 'running';
    this.#set(0.08);
    this.#tick();
  }

  /** Slows down as it nears 90%: honest motion without a fake finish */
  #tick() {
    clearTimeout(this.#trickle);
    this.#trickle = setTimeout(() => {
      if (this.#tasks === 0) return;
      this.#set(this.#value + (0.9 - this.#value) * 0.12);
      this.#tick();
    }, 220);
  }

  done() {
    if (this.#tasks === 0) return;
    this.#tasks -= 1;
    if (this.#tasks > 0) return;
    this.#finish();
  }

  reset() {
    this.#tasks = 0;
    this.#finish();
  }

  #finish() {
    clearTimeout(this.#trickle);
    clearTimeout(this.#safety);
    if (!this.#el) return;
    this.#set(1);
    this.#el.dataset.state = 'done';
  }

  /** @param {number} value */
  #set(value) {
    this.#value = Math.min(value, 1);
    this.#bar.style.setProperty('--q-progress', String(this.#value));
  }
}

const progress = new ProgressBar();

/**
 * Shows the bar and marks `region` busy until `promise` settles
 * @param {Promise<unknown> | undefined} promise
 * @param {Element | null} region
 */
const track = (promise, region) => {
  if (!promise) return;
  progress.start();
  const timer = setTimeout(() => {
    region?.setAttribute('data-q-busy', '');
    region?.setAttribute('aria-busy', 'true');
  }, REGION_DELAY_MS);
  const safety = setTimeout(finish, MAX_BUSY_MS);
  let finished = false;
  function finish() {
    if (finished) return;
    finished = true;
    clearTimeout(timer);
    clearTimeout(safety);
    region?.removeAttribute('data-q-busy');
    region?.removeAttribute('aria-busy');
    progress.done();
  }
  promise.then(finish, finish);
};

/** @param {Event} event */
const promiseOf = (event) => /** @type {{ promise?: Promise<unknown> }} */ (/** @type {unknown} */ (event)).promise;

/* 2. In-page updates ------------------------------------------------------ */

// Product option change: the product (section, quick-add pop-up or card)
document.addEventListener(
  StandardEvents.productSelect,
  (event) => {
    const source = event.target instanceof Element ? event.target : null;
    // Cards swap swatches locally — only full product forms need the guard
    if (!source || source.closest('product-card')) return;
    track(promiseOf(event), source.closest('dialog, .shopify-section'));
  },
  true
);

// Filters and sort on collection and search pages
for (const type of [StandardEvents.collectionUpdate, StandardEvents.searchUpdate]) {
  document.addEventListener(
    type,
    (event) => {
      const source = event.target instanceof Element ? event.target : null;
      // Predictive search in the header has its own indicator
      if (!source || source.closest('#header-component, .search-modal')) return;
      track(promiseOf(event), source.closest('.shopify-section'));
    },
    true
  );
}

/* 1. Page changes --------------------------------------------------------- */

/** @param {URL} url */
const isPageChange = (url) =>
  url.origin === location.origin &&
  !(url.pathname === location.pathname && url.search === location.search && url.hash !== '');

/** @type {any} */
const navigationApi = /** @type {any} */ (window).navigation;

if (navigationApi && 'addEventListener' in navigationApi) {
  navigationApi.addEventListener('navigate', (/** @type {any} */ event) => {
    // Same-document changes (filters' pushState) are covered above
    if (event.hashChange || event.downloadRequest || event.destination?.sameDocument) return;
    if (event.navigationType === 'reload') return;
    const url = new URL(event.destination.url);
    if (url.origin !== location.origin) return;
    // Bottom-nav tabs and product / listing links on phones show their own skeleton
    // screen instead (assets/custom-tab-bar.js marks html[data-q-tab-nav] in a later
    // listener of this same event, so decide once this dispatch is over). A task, not
    // a microtask: for a real tap the browser dispatches with an empty JS stack, so a
    // microtask ran between the two listeners and the bar started over the skeleton.
    setTimeout(() => {
      if (!document.documentElement.hasAttribute('data-q-tab-nav')) progress.start();
    }, 0);
  });
  // A page change that was stopped (e.g. the shopper tapped another bottom tab, assets/custom-tab-bar.js)
  navigationApi.addEventListener('navigateerror', () => progress.reset());
} else {
  // Fallback: plain link clicks (after every other handler had its say)
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement) || link.target === '_blank' || link.hasAttribute('download')) return;
    if (link.closest('q-tab-bar')) return; // bottom-nav tabs show their own skeleton screen
    if (isPageChange(new URL(link.href))) progress.start();
  });
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (event.defaultPrevented || !(form instanceof HTMLFormElement) || form.target === '_blank') return;
    if (form.closest('product-form-component')) return;
    if (isPageChange(new URL(form.action || location.href))) progress.start();
  });
}

// Back / forward restored this page from memory: nothing is loading
window.addEventListener('pageshow', (event) => {
  if (event.persisted) progress.reset();
});
